import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Clock, FileCheck, Send, AlertCircle, Calendar } from "lucide-react";
import { DSRAttachments } from "@/components/dpo/DSRAttachments";

interface Event {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  received: "Recebida",
  under_review: "Em análise",
  in_progress: "Em andamento",
  fulfilled: "Concluída",
  rejected: "Rejeitada",
  extended: "Prazo prorrogado",
};

function eventLabel(ev: Event): { icon: JSX.Element; label: string; detail?: string } | null {
  switch (ev.event_type) {
    case "received":
      return { icon: <Send className="w-4 h-4 text-muted-foreground" />, label: "Solicitação recebida" };
    case "status_changed": {
      const to = String(ev.payload?.to ?? "");
      const from = String(ev.payload?.from ?? "");
      // Oculta eventos sem mudança real
      if (!to || to === from) return null;
      const toLabel = STATUS_LABELS[to] ?? to;
      // Mensagens humanizadas por status alvo
      if (to === "fulfilled") {
        return { icon: <FileCheck className="w-4 h-4 text-success" />, label: "Solicitação concluída" };
      }
      if (to === "rejected") {
        return { icon: <AlertCircle className="w-4 h-4 text-destructive" />, label: "Solicitação não atendida" };
      }
      if (to === "under_review") {
        return { icon: <FileCheck className="w-4 h-4 text-muted-foreground" />, label: "Em análise pelo Encarregado" };
      }
      if (to === "in_progress") {
        return { icon: <FileCheck className="w-4 h-4 text-muted-foreground" />, label: "Em andamento" };
      }
      if (to === "extended") {
        return { icon: <Calendar className="w-4 h-4 text-warning" />, label: "Prazo prorrogado" };
      }
      return { icon: <FileCheck className="w-4 h-4 text-muted-foreground" />, label: `Status atualizado para ${toLabel}` };
    }
    case "deadline_extended":
    case "due_extended":
    case "extended":
      return {
        icon: <Calendar className="w-4 h-4 text-warning" />,
        label: "Prazo prorrogado",
        detail: ev.payload?.extended_due_at
          ? `Novo prazo: ${format(new Date(String(ev.payload.extended_due_at)), "dd/MM/yyyy")}`
          : undefined,
      };
    case "responded":
      return {
        icon: <FileCheck className="w-4 h-4 text-success" />,
        label: "Resposta enviada",
        detail: typeof ev.payload?.resolution === "string"
          ? String(ev.payload.resolution).slice(0, 300)
          : undefined,
      };
    case "attachment_added":
      return {
        icon: <FileCheck className="w-4 h-4 text-muted-foreground" />,
        label: "Documento anexado",
        detail: typeof ev.payload?.filename === "string" ? String(ev.payload.filename) : undefined,
      };
    default:
      // Eventos internos (created, note, assigned) não devem aparecer publicamente.
      // Caso vazem, ocultamos para não exibir texto técnico.
      return null;
  }
}

export function DSRTimeline({ requestId }: { requestId: string }) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("data_subject_request_events")
        .select("id, event_type, payload, created_at")
        .eq("request_id", requestId)
        .order("created_at", { ascending: true });
      if (!cancelled) {
        setEvents((data as Event[]) ?? []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  if (loading) return <Skeleton className="h-16 w-full" />;
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground">Sem eventos públicos ainda.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {events.map((ev) => {
          const meta = eventLabel(ev);
          if (!meta) return null;
          return (
            <div key={ev.id} className="flex gap-2 text-xs">
              <div className="mt-0.5">{meta.icon}</div>
              <div className="flex-1">
                <p className="font-medium">{meta.label}</p>
                {meta.detail && <p className="text-muted-foreground">{meta.detail}</p>}
                <p className="text-[10px] text-muted-foreground">
                  <Clock className="inline w-3 h-3 mr-1" />
                  {format(new Date(ev.created_at), "dd/MM/yyyy HH:mm")}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="pt-2 border-t">
        <DSRAttachments requestId={requestId} canManage={false} />
      </div>
    </div>
  );
}
